import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { inr } from "../../lib/format";
import { Field, BTN } from "../Common";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const FORMULA = { basic_pct: 50, hra_pct: 40, pf_pct: 12, pf_wage_cap: 15000, pt_amount: 200, pt_threshold: 15000, tds_auto: true };
const EMPTY = { name: "", legal_name: "", address: "", cin: "", pan: "", tan: "", pf_registration: "", weekly_offs: [5, 6], payroll: FORMULA };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const offLabel = (offs) => (offs?.length ? offs.map((d) => DAYS[d]).join(", ") : "None");
const INFO = [["name", "Short name"], ["legal_name", "Legal name (on payslips)"], ["address", "Registered address"], ["cin", "CIN"], ["pan", "PAN"], ["tan", "TAN"], ["pf_registration", "PF registration no."]];
const NUMS = [["basic_pct", "Basic (% of gross)"], ["hra_pct", "HRA (% of Basic)"], ["pf_pct", "PF (% of Basic)"], ["pf_wage_cap", "PF wage cap (₹, 0 = no cap)"], ["pt_amount", "Professional tax (₹/month)"], ["pt_threshold", "PT applies above gross (₹)"]];

const EntityDialog = ({ open, onOpenChange, entity, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (open) setF(entity ? { ...EMPTY, ...entity, weekly_offs: entity.weekly_offs ?? [5, 6], payroll: { ...FORMULA, ...entity.payroll } } : EMPTY);
  }, [open, entity]);
  const toggleDay = (d) => setF({ ...f, weekly_offs: f.weekly_offs.includes(d) ? f.weekly_offs.filter((x) => x !== d) : [...f.weekly_offs, d].sort() });
  const setP = (k, v) => setF({ ...f, payroll: { ...f.payroll, [k]: v } });
  const submit = async (e) => {
    e.preventDefault();
    const payroll = Object.fromEntries(Object.entries(f.payroll).map(([k, v]) => [k, k === "tds_auto" ? v : Number(v) || 0]));
    try {
      if (entity) await api.put(`/entities/${entity.id}`, { ...f, payroll });
      else await api.post("/entities", { ...f, payroll });
      toast.success("Entity saved");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="entity-dialog">
      <DialogHeader><DialogTitle>{entity ? "Edit entity" : "Add entity"}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          {INFO.map(([k, l]) => (
            <Field key={k} label={l} className={k === "address" ? "sm:col-span-2" : ""}>
              <Input required={k === "name"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`entity-${k}-input`} />
            </Field>
          ))}
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#E08A1E] mb-2">Weekly off days</div>
          <p className="text-xs text-[#6E6E73] mb-3">These days are not counted as leave days or unpaid-leave deductions.</p>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`entity-weekoff-${d.toLowerCase()}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${f.weekly_offs.includes(i) ? "bg-[#0A0807] text-white border-[#0A0807]" : "bg-white text-[#1D1D1F] border-[#E5E5EA] hover:border-[#E08A1E]"}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#E08A1E] mb-3">Payroll formula</div>
          <div className="grid sm:grid-cols-3 gap-3">
            {NUMS.map(([k, l]) => (
              <Field key={k} label={l}><Input type="number" min="0" step="any" value={f.payroll[k]} onChange={(e) => setP(k, e.target.value)} data-testid={`entity-${k}-input`} /></Field>
            ))}
          </div>
          <label className="flex items-center gap-3 mt-4 text-sm"><Switch checked={f.payroll.tds_auto} onCheckedChange={(v) => setP("tds_auto", v)} data-testid="entity-tds-switch" />Estimate TDS automatically (new tax regime)</label>
        </div>
        <Button type="submit" className={`w-full ${BTN}`} data-testid="entity-save-button">Save entity</Button>
      </form>
    </DialogContent></Dialog>
  );
};

export const EntitiesTab = () => {
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState({ open: false, entity: null });
  const load = () => api.get("/entities").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = async (e) => {
    try { await api.delete(`/entities/${e.id}`); toast.success("Entity deleted"); load(); } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <div data-testid="entities-tab">
      <div className="flex justify-between items-center mb-4 gap-2">
        <p className="text-sm text-[#6E6E73]">Each company has its own payslip header and payroll formula. Assign employees to an entity on their profile.</p>
        <Button onClick={() => setDlg({ open: true, entity: null })} className={BTN} data-testid="add-entity-button"><Plus className="h-4 w-4 mr-1" />Add entity</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {list.map((e) => (
          <div key={e.id} className="bg-white border border-[#E5E5EA] rounded-2xl p-5" data-testid={`entity-card-${e.id}`}>
            <div className="flex justify-between gap-2">
              <div className="flex gap-3">
                <div className="h-10 w-10 rounded-xl bg-[#F7EEE2] flex items-center justify-center"><Building2 className="h-5 w-5 text-[#C87515]" /></div>
                <div><div className="font-bold">{e.name}</div><div className="text-xs text-[#6E6E73]">{e.legal_name} · {e.employee_count} active employees</div></div>
              </div>
              <div>
                <Button size="icon" variant="ghost" onClick={() => setDlg({ open: true, entity: e })} data-testid={`edit-entity-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(e)} data-testid={`delete-entity-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="text-xs text-[#6E6E73] mt-3">{e.address || "No address"}{e.cin && ` · CIN ${e.cin}`}</div>
            <div className="text-xs mt-2" data-testid={`entity-weekoffs-${e.id}`}>Weekly off: <b>{offLabel(e.weekly_offs)}</b></div>
            <div className="text-xs mt-3 rounded-lg bg-[#F5F5F7] p-2.5">
              Basic {e.payroll.basic_pct}% · HRA {e.payroll.hra_pct}% of Basic · PF {e.payroll.pf_pct}%{e.payroll.pf_wage_cap ? ` (cap ${inr(e.payroll.pf_wage_cap)})` : ""} · PT {inr(e.payroll.pt_amount)} · TDS {e.payroll.tds_auto ? "auto" : "off"}
            </div>
          </div>
        ))}
      </div>
      <EntityDialog open={dlg.open} entity={dlg.entity} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </div>
  );
};
